import { isEmpty } from '@ember/utils'
import fillMonths from 'district-ui-client/utils/student-events/fill-months'
import type { StatsScope } from 'district-ui-client/utils/student-events/stats-scope'
import type StudentEventsService from 'district-ui-client/services/student-events'
import type { NormalizedInterval } from 'date-fns'

export interface AwardsEarnedTotalsResponse {
  awards_earned_totals_summary_by_grade: SummaryData[]
  awards_earned_totals_by_grade_by_month: MonthData[]
}

export interface SummaryData {
  grade_position: number
  data: [
    { category: 'gold'; count: number },
    { category: 'silver'; count: number },
    { category: 'bronze'; count: number },
  ]
}

interface MonthData {
  grade_position: number
  months: { gold: number; silver: number; bronze: number; month: string }[]
}

export interface AwardsEarnedTotalsByGradeData {
  grade_position: number
  summary: {
    gold: number
    silver: number
    bronze: number
  }
  months: {
    gold: number
    silver: number
    bronze: number
    month: string
  }[]
}

export async function awardsEarnedTotals(
  this: StudentEventsService,
  product: LegacyProductRe | LegacyProductRex | LegacyProductMs,
  statsScope: StatsScope,
  period: NormalizedInterval,
): Promise<Nullable<AwardsEarnedTotalsByGradeData[]>> {
  if (isEmpty(statsScope.ids)) return null

  const url = this.buildUrl('awards_earned_totals_by_grade_by_month', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<AwardsEarnedTotalsResponse>(url, query)
  if (!data) return null

  const { awards_earned_totals_summary_by_grade: summaryData, awards_earned_totals_by_grade_by_month: monthData } = data
  if (summaryData.length === 0 || monthData.length === 0) {
    return null
  }

  return groupByGrade(summaryData, monthData, period)
}

function groupByGrade(summaryData: SummaryData[], monthData: MonthData[], period: NormalizedInterval) {
  const sortedSummaryData = [...summaryData].sort((a, b) => a.grade_position - b.grade_position)

  return sortedSummaryData
    .map((summary) => {
      const gradeMonthData = monthData.find((month) => month.grade_position === summary.grade_position)
      if (!gradeMonthData) return null

      const goldCount = summary.data.find((value) => value.category === 'gold')?.count ?? 0
      const silverCount = summary.data.find((value) => value.category === 'silver')?.count ?? 0
      const bronzeCount = summary.data.find((value) => value.category === 'bronze')?.count ?? 0
      const fullMonthData = fillMonths(gradeMonthData.months, period, {
        gold: 0,
        silver: 0,
        bronze: 0,
      })

      return {
        grade_position: summary.grade_position,
        summary: {
          gold: goldCount,
          silver: silverCount,
          bronze: bronzeCount,
        },
        months: fullMonthData,
      }
    })
    .filter((data) => data !== null)
}
